import {gsap} from 'gsap';
import {ScrollTrigger} from 'gsap/ScrollTrigger.js';
gsap.registerPlugin( ScrollTrigger );

( function( app ) {
  const scrollTriggers = [];
  let topOffsetMeasure;
  let lastOffset = 0;
  let offsetRefreshTimer;

  const getTopOffset = () => {
    const header = document.querySelector( '.navigation-one' );

    if ( header ) {
      return Math.round( header.getBoundingClientRect().bottom );
    }

    if ( !topOffsetMeasure ) {
      topOffsetMeasure = document.createElement( 'div' );
      topOffsetMeasure.setAttribute( 'aria-hidden', 'true' );
      topOffsetMeasure.style.cssText = [
        'position:absolute',
        'visibility:hidden',
        'pointer-events:none',
        'height:var(--topOffset)',
        'width:0',
        'overflow:hidden',
      ].join( ';' );
      document.body.appendChild( topOffsetMeasure );
    }

    return Math.round( topOffsetMeasure.getBoundingClientRect().height );
  };

  const isDesktop = () => window.innerWidth >= 1024;

  const getPinnedHeight = () => Math.max( window.innerHeight - getTopOffset(), 0 );

  const setPinnedHeights = () => {
    document.querySelectorAll( '[data-component="tabbed-video-content"] .js-tabbed-video-pin' ).forEach( ( pin ) => {
      const pinnedHeight = `${getPinnedHeight()}px`;

      pin.style.setProperty( '--tabbedVideoPinnedHeight', pinnedHeight );
      pin.style.minHeight = isDesktop() ? pinnedHeight : '';
      pin.style.height = isDesktop() ? pinnedHeight : '';
    } );
  };

  const refreshPinnedSections = () => {
    clearTimeout( offsetRefreshTimer );

    offsetRefreshTimer = setTimeout( () => {
      setPinnedHeights();

      scrollTriggers.forEach( ( trigger ) => {
        if ( trigger && trigger.pin ) {
          trigger.refresh();
        }
      } );

      ScrollTrigger.refresh( true );
    }, 50 );
  };

  const afterInitialPaint = ( callback ) => {
    const run = () => {
      requestAnimationFrame( () => {
        requestAnimationFrame( callback );
      } );
    };

    const runWhenIdle = () => {
      if ( 'requestIdleCallback' in window ) {
        window.requestIdleCallback( run, {timeout: 1200} );
      } else {
        window.setTimeout( run, 600 );
      }
    };

    if ( document.readyState === 'complete' ) {
      runWhenIdle();
    } else {
      window.addEventListener( 'load', runWhenIdle, {once: true} );
    }
  };

  const watchTopOffset = () => {
    const check = () => {
      const current = getTopOffset();

      if ( current !== lastOffset ) {
        lastOffset = current;
        refreshPinnedSections();
      }

      requestAnimationFrame( check );
    };

    check();
  };

  const COMPONENT = {
    init() {
      setPinnedHeights();

      afterInitialPaint( () => {
        this.initTabbedVideoContent();
      } );
    },

    initTabbedVideoContent() {
      document
          .querySelectorAll( '[data-component="tabbed-video-content"]' )
          .forEach( ( c ) => this.initTabs( c ) );

      setPinnedHeights();
      lastOffset = getTopOffset();
      watchTopOffset();

      let resizeTimer;

      window.addEventListener( 'resize', () => {
        clearTimeout( resizeTimer );

        resizeTimer = setTimeout( () => {
          setPinnedHeights();

          ScrollTrigger.refresh( true );
        }, 250 );
      } );
    },

    initTabs( component ) {
      const tabGroups = Array.from( component.querySelectorAll( '.js-tab-group' ) );

      if ( !tabGroups.length ) return;

      const slides = Array.from( component.querySelectorAll( '[role="tabpanel"]' ) );

      if ( !slides.length ) return;

      const tabSets = tabGroups.map( ( tabGroup ) => {
        const triggers = Array.from(
            tabGroup.querySelectorAll( '[role="tab"]' ),
        );

        return triggers.map( ( trigger ) => {
          const li = trigger.closest( 'li' );

          return {
            trigger,
            line: li ? li.querySelector( ':scope .js-tab-progress' ) : null,
            desc: trigger.querySelector( '.tabbed-video-description-wrapper' ),
          };
        } );
      } );

      const allItems = tabSets.flat();

      const setState = ( index, progress = 0 ) => {
        tabSets.forEach( ( items ) => {
          items.forEach( ( {trigger, line, desc}, i ) => {
            const isActive = i === index;

            trigger.dataset.active = isActive ? 'true' : 'false';
            trigger.setAttribute( 'aria-selected', isActive ? 'true' : 'false' );
            trigger.tabIndex = isActive ? 0 : -1;

            trigger.classList.toggle( 'opacity-100', isActive );
            trigger.classList.toggle( 'opacity-50', !isActive );

            if ( desc ) {
              desc.style.maxHeight = isActive ? desc.scrollHeight + 'px' : '0px';
              desc.style.opacity = isActive ? '1' : '0';
              desc.style.transform = isActive ? 'translateY(0)' : 'translateY(6px)';
            }

            if ( line ) {
              line.style.height = isActive ? `${progress * 100}%` : '0%';
              line.style.opacity = isActive ? '1' : '0.4';
            }
          } );
        } );

        slides.forEach( ( slide, i ) => {
          const isActive = i === index;

          slide.classList.toggle( 'opacity-100', isActive );
          slide.classList.toggle( 'opacity-0', !isActive );
          slide.setAttribute( 'aria-hidden', isActive ? 'false' : 'true' );
        } );
      };

      const updateFromScroll = ( scrollProgress ) => {
        const tabCount = slides.length;
        const scaledProgress = Math.min( scrollProgress * tabCount, tabCount - 0.0001 );
        const index = Math.floor( scaledProgress );
        const tabProgress = scaledProgress - index;

        setState( index, tabProgress );
      };

      setState( 0, 0 );

      const isMobile = () => window.innerWidth < 1024;
      const desktopPinTarget = component.querySelector( '.js-tabbed-video-pin' ) || component;
      const pinTarget = desktopPinTarget;
      const getPinDistance = () => isMobile() ?
        ( window.innerHeight - getTopOffset() ) * ( slides.length + 0.35 ) :
        pinTarget.offsetHeight * slides.length;

      const scrollTrigger = ScrollTrigger.create( {
        trigger: pinTarget,
        start: () => `top top+=${getTopOffset()}`,
        end: () => `+=${getPinDistance()}`,
        pin: pinTarget,
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: ( self ) => updateFromScroll( self.progress ),
        onLeave: () => setState( slides.length - 1, 1 ),
        onLeaveBack: () => setState( 0, 0 ),
      } );

      scrollTriggers.push( scrollTrigger );

      allItems.forEach( ( {trigger}, index ) => {
        trigger.addEventListener( 'click', ( event ) => {
          event.preventDefault();

          const tabIndex = Number( trigger.dataset.tabTrigger || index % slides.length );
          const scrollDistance = scrollTrigger.end - scrollTrigger.start;
          const targetProgress = tabIndex / slides.length + 0.001;

          window.scrollTo( {
            top: scrollTrigger.start + scrollDistance * targetProgress,
            behavior: 'smooth',
          } );
        } );
      } );
    },
  };

  app.registerComponent( 'tabbed-video-content', COMPONENT );
} )( app );
